import { GQL } from '@aline/graphql';
import { z } from 'zod';

import type { QuestionWithParents } from './question-dependency-graph';
import {
  getHasQuestionDependencyCycle,
  getQuestionDependencyCycleErrorMessage,
} from './question-dependency-graph';

/**
 * Zod schemas for the columns and filters of a report the report agent
 * proposes (see `ReportPlanSchema`). Column data types and file-field
 * identifiers bind to the GraphQL enums so a schema change propagates with no
 * manual drift.
 */
export const SELECT_EXTRACTION_QUESTION_TYPES = [
  GQL.ExtractionQuestionType.SingleSelect,
  GQL.ExtractionQuestionType.MultiSelect,
] as const;

export function getIsSelectExtractionQuestionType(
  type: GQL.ExtractionQuestionType
): type is (typeof SELECT_EXTRACTION_QUESTION_TYPES)[number] {
  return SELECT_EXTRACTION_QUESTION_TYPES.some(selectType => selectType === type);
}

export const reportPreviewColumnSchema = z.object({
  existingQuestionId: z.string().uuid().optional(),
  inputColumnKeys: z.array(z.string().min(1)).optional().default([]),
  key: z.string().min(1),
  options: z.array(z.string().min(1)).optional(),
  questionText: z.string(),
  title: z.string(),
  type: z.nativeEnum(GQL.ExtractionQuestionType),
});

export function reportPreviewColumnsToQuestionGraph(
  columns: readonly Pick<ReportPreviewColumn, 'inputColumnKeys' | 'key'>[]
): QuestionWithParents[] {
  return columns.map(column => ({ id: column.key, parentIds: column.inputColumnKeys }));
}

function refineReportPreviewColumns(
  columns: Array<z.output<typeof reportPreviewColumnSchema>>,
  ctx: z.RefinementCtx
) {
  const seenKeys = new Set<string>();
  for (const [index, column] of columns.entries()) {
    if (seenKeys.has(column.key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate column key "${column.key}"`,
        path: [index, 'key'],
      });
    }
    seenKeys.add(column.key);
  }

  for (const [index, column] of columns.entries()) {
    for (const inputKey of column.inputColumnKeys) {
      if (inputKey === column.key) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A column cannot depend on itself',
          path: [index, 'inputColumnKeys'],
        });
      } else if (!seenKeys.has(inputKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Unknown input column key "${inputKey}"`,
          path: [index, 'inputColumnKeys'],
        });
      }
    }

    const isSelect = getIsSelectExtractionQuestionType(column.type);
    if (isSelect && !column.existingQuestionId) {
      if (!column.options?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select columns require options',
          path: [index, 'options'],
        });
      } else {
        const labels = new Set<string>();
        for (const [optionIndex, option] of column.options.entries()) {
          const normalized = option.trim().toLowerCase();
          if (labels.has(normalized)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Duplicate option "${option}"`,
              path: [index, 'options', optionIndex],
            });
          }
          labels.add(normalized);
        }
      }
    } else if (!isSelect && column.options && column.options.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Options are only allowed on select columns',
        path: [index, 'options'],
      });
    }
  }

  if (getHasQuestionDependencyCycle(reportPreviewColumnsToQuestionGraph(columns))) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: getQuestionDependencyCycleErrorMessage(),
      path: ['inputColumnKeys'],
    });
  }
}

export const reportPreviewColumnsSchema = z
  .array(reportPreviewColumnSchema)
  .superRefine(refineReportPreviewColumns);

/**
 * Operators the AI report agent may emit in a preview filter. Single source
 * of truth: the tool input schema, the resolver's translator (exhaustive
 * `satisfies Record<…>`), the client filter editor's per-field operator
 * menus, and the system prompt's operators table all reference this list.
 *
 * Add a new operator here and the compiler will flag every place that has
 * to grow with it.
 */
export const REPORT_PREVIEW_OPERATORS = [
  'contains',
  'equals',
  'startsWith',
  'endsWith',
  'before',
  'after',
  /**
   * `is` is the exact/membership match. On date fields it is the grid's
   * exact-date operator (`equals` would be rejected by the date filter
   * discriminator). On entity-valued fields (status, type, owner, labels,
   * parties, fileType, requesterFavorited) the plan carries a human-readable
   * value (status label, contract-type name, email, label text, party name,
   * file-type label, boolean string) and the translator resolves it to org
   * rows and rewrites the operator to the field's grid operator
   * (`CustomOperatorValue.Owner` etc.) — the same rewrite `isEmpty` gets.
   * Operators stay semantic here; grid dispatch strings never enter the
   * AI-facing vocabulary.
   */
  'is',
  /**
   * Multi-value variants of `is` on entity-valued fields: the value is an
   * array of the same per-field name strings `is` takes, and the translator
   * resolves every entry (all or nothing) before emitting the grid's any-of
   * operator (`owner`, `label`, …) or its negation (`owner-not-any-of`, …).
   * `isNoneOf` is unavailable on `status` — the grid has no negated
   * document-status operator.
   */
  'isAnyOf',
  'isNoneOf',
  'isEmpty',
  'isNotEmpty',
] as const;
export type ReportPreviewOperator = (typeof REPORT_PREVIEW_OPERATORS)[number];

/** Operators that carry no `value` payload. */
export const REPORT_PREVIEW_NO_VALUE_OPERATORS = [
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly ReportPreviewOperator[];
export type ReportPreviewNoValueOperator = (typeof REPORT_PREVIEW_NO_VALUE_OPERATORS)[number];

export function getIsNoValueOperator(
  op: ReportPreviewOperator
): op is ReportPreviewNoValueOperator {
  return REPORT_PREVIEW_NO_VALUE_OPERATORS.some(noValueOp => noValueOp === op);
}

/** Operators whose `value` is an array of name strings rather than one string. */
export const REPORT_PREVIEW_MULTI_VALUE_OPERATORS = [
  'isAnyOf',
  'isNoneOf',
] as const satisfies readonly ReportPreviewOperator[];
export type ReportPreviewMultiValueOperator = (typeof REPORT_PREVIEW_MULTI_VALUE_OPERATORS)[number];

export function getIsMultiValueOperator(
  op: ReportPreviewOperator
): op is ReportPreviewMultiValueOperator {
  return REPORT_PREVIEW_MULTI_VALUE_OPERATORS.some(multiValueOp => multiValueOp === op);
}

const REPORT_PREVIEW_OPERATOR_SET = new Set<string>(REPORT_PREVIEW_OPERATORS);

/**
 * Narrow an arbitrary string (e.g. a raw `<select>` value or an LLM-supplied
 * filter operator) to a {@link ReportPreviewOperator}. Shared by the server
 * resolver and the client filter editor so both validate against the same list.
 */
export function getIsReportPreviewOperator(value: string): value is ReportPreviewOperator {
  return REPORT_PREVIEW_OPERATOR_SET.has(value);
}

/**
 * Filter shape stays loose on `value` because the type varies by operator,
 * but `operator` is constrained to {@link REPORT_PREVIEW_OPERATORS} so the
 * LLM's choices are validated at the tool boundary.
 */
export const reportPreviewFilterSchema = z.object({
  field: z.string(),
  operator: z.enum(REPORT_PREVIEW_OPERATORS),
  value: z.unknown().optional(),
});

export type ReportPreviewColumn = z.infer<typeof reportPreviewColumnSchema>;
export type ReportPreviewFilter = z.infer<typeof reportPreviewFilterSchema>;

/**
 * Built-in file-field columns every AI-created report always includes, so an
 * agent-built report ships with the same base columns as a manually-created
 * one. Keep in sync with `createDefaultReportState` (Name, Status, Type,
 * Labels) — `Type` is the contract-type column, not the file-extension one.
 */
export const DEFAULT_AI_REPORT_FILE_FIELDS: GQL.ReportColumnFileField[] = [
  GQL.ReportColumnFileField.Name,
  GQL.ReportColumnFileField.Status,
  GQL.ReportColumnFileField.Type,
  GQL.ReportColumnFileField.Labels,
];

/**
 * Resolve the final file-field set for an AI report. The agent's emitted
 * `fileFields` are ADDITIVE to the defaults, never a replacement: the report
 * always keeps the default built-in columns and appends any extra field the
 * user asked for (e.g. parties). Name is pinned first. Applied on the approval
 * card (so the card matches what will save) and again when the approved plan
 * is persisted. The approval card can edit AI extraction columns, but these
 * built-in columns remain fixed and are always included.
 */
export function resolveAiReportFileFields(
  inputFileFields: readonly GQL.ReportColumnFileField[] | null | undefined
): GQL.ReportColumnFileField[] {
  return [
    GQL.ReportColumnFileField.Name,
    ...DEFAULT_AI_REPORT_FILE_FIELDS.filter(f => f !== GQL.ReportColumnFileField.Name),
    ...(inputFileFields ?? []).filter(
      f => f !== GQL.ReportColumnFileField.Name && !DEFAULT_AI_REPORT_FILE_FIELDS.includes(f)
    ),
  ];
}

/**
 * Human-readable description of each `ReportColumnFileField` for the report
 * agent system prompt. Typed as an exhaustive Record so adding a new enum
 * member is a compile error here, forcing prompt updates in lockstep with
 * schema changes.
 */
export const REPORT_COLUMN_FILE_FIELD_DESCRIPTIONS: Record<GQL.ReportColumnFileField, string> = {
  [GQL.ReportColumnFileField.Id]: 'document UUID',
  [GQL.ReportColumnFileField.Name]: 'document name (always included implicitly)',
  [GQL.ReportColumnFileField.Status]:
    'document lifecycle status (Draft, Signed, Template, Expired)',
  [GQL.ReportColumnFileField.Type]: 'contract type classification',
  [GQL.ReportColumnFileField.FileType]: 'file format of the upload (e.g., PDF, DOCX)',
  [GQL.ReportColumnFileField.Owner]: 'user who owns the document',
  [GQL.ReportColumnFileField.Parties]: 'counterparties on the document',
  [GQL.ReportColumnFileField.RequesterFavorited]:
    'whether the requester has favorited the document',
  [GQL.ReportColumnFileField.Labels]: 'labels applied to the document',
  [GQL.ReportColumnFileField.CreatedAt]: 'creation timestamp',
  [GQL.ReportColumnFileField.UpdatedAt]: 'last-modified timestamp',
  [GQL.ReportColumnFileField.DeletedAt]: 'soft-delete timestamp (null if active)',
  [GQL.ReportColumnFileField.SignedAt]: 'signed timestamp',
  [GQL.ReportColumnFileField.LastAccessedByRequesterAt]: 'last time the requester viewed it',
};

/**
 * Human-readable description of each `ReportColumnDataType` for the report
 * grid. Plan columns use {@link EXTRACTION_QUESTION_TYPE_DESCRIPTIONS}.
 */
export const REPORT_COLUMN_DATA_TYPE_DESCRIPTIONS: Record<GQL.ReportColumnDataType, string> = {
  [GQL.ReportColumnDataType.String]: 'free-text string value',
  [GQL.ReportColumnDataType.Number]: 'integer or decimal',
  [GQL.ReportColumnDataType.Date]: 'date only, no time component',
  [GQL.ReportColumnDataType.DateTime]: 'date with time component',
  [GQL.ReportColumnDataType.Boolean]: 'yes/no',
  [GQL.ReportColumnDataType.SingleSelect]: 'one option from a fixed list',
};

/**
 * Human-readable description of each extraction-question type the report
 * agent may emit on a plan column.
 */
export const EXTRACTION_QUESTION_TYPE_DESCRIPTIONS: Record<GQL.ExtractionQuestionType, string> = {
  [GQL.ExtractionQuestionType.Text]: 'free-text string value',
  [GQL.ExtractionQuestionType.Number]: 'integer or decimal',
  [GQL.ExtractionQuestionType.Date]: 'date only, no time component',
  [GQL.ExtractionQuestionType.Boolean]: 'yes/no',
  [GQL.ExtractionQuestionType.SingleSelect]: 'exactly one option from a closed list',
  [GQL.ExtractionQuestionType.MultiSelect]: 'one or more options from a closed list',
};

/**
 * Per-field operator menu the LLM may pick from. Centralised here so the
 * prompt, the filter editor, and any future tightening of the schema all
 * agree on which operator/field combinations are legal. Default operator
 * for each field is whichever operator the existing UI picks first.
 *
 * `signedAt` is deliberately absent: it is Aline's internal workflow
 * timestamp, not a reliable contract signature date, and filtering on it
 * misleads both users and the agent. `deletedAt` is absent because AI
 * reports never cover soft-deleted documents.
 */
export const REPORT_PREVIEW_FILTER_FIELDS = [
  {
    defaultOperator: 'contains',
    field: 'name',
    label: 'Document name',
    operators: ['contains', 'equals', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty'],
  },
  {
    defaultOperator: 'contains',
    field: 'description',
    label: 'Description',
    operators: ['contains', 'equals', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty'],
  },
  /**
   * Value is a status label (Draft, Signed, Template, Expired). No
   * `isNoneOf`: the grid has no negated document-status operator.
   */
  {
    defaultOperator: 'is',
    field: 'status',
    label: 'Status',
    operators: ['is', 'isAnyOf', 'isEmpty', 'isNotEmpty'],
  },
  /** Value is a contract type name. */
  {
    defaultOperator: 'is',
    field: 'type',
    label: 'Contract type',
    operators: ['is', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
  },
  {
    defaultOperator: 'after',
    field: 'createdAt',
    label: 'Created',
    operators: ['before', 'after', 'is', 'isEmpty', 'isNotEmpty'],
  },
  {
    defaultOperator: 'after',
    field: 'updatedAt',
    label: 'Updated',
    operators: ['before', 'after', 'is', 'isEmpty', 'isNotEmpty'],
  },
  {
    defaultOperator: 'after',
    field: 'lastAccessedByRequesterAt',
    label: 'Last accessed',
    operators: ['before', 'after', 'is', 'isEmpty', 'isNotEmpty'],
  },
  /** Value is the owning member's email — unique per user, unlike display names. */
  {
    defaultOperator: 'is',
    field: 'owner',
    label: 'Owner',
    operators: ['is', 'isAnyOf', 'isNoneOf'],
  },
  /** Value is a label's exact text. */
  {
    defaultOperator: 'is',
    field: 'labels',
    label: 'Labels',
    operators: ['is', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
  },
  /** Value is a party name, matched on the normalized-name key. */
  {
    defaultOperator: 'is',
    field: 'parties',
    label: 'Parties',
    operators: ['is', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
  },
  /** Value is a file-type display label from `FILE_TYPE_FILTER_OPTIONS` (e.g. "PDF"). */
  {
    defaultOperator: 'is',
    field: 'fileType',
    label: 'File type',
    operators: ['is', 'isAnyOf', 'isNoneOf'],
  },
  /** Value is the string 'true' or 'false'; the grid's boolean filter takes `is`. */
  {
    defaultOperator: 'is',
    field: 'requesterFavorited',
    label: 'Favorited',
    operators: ['is'],
  },
] as const satisfies readonly {
  defaultOperator: ReportPreviewOperator;
  field: string;
  label: string;
  operators: readonly ReportPreviewOperator[];
}[];

export type ReportPreviewFilterField = (typeof REPORT_PREVIEW_FILTER_FIELDS)[number]['field'];

/**
 * Human-readable label for each `DocumentStatus` enum value. Sourcing labels
 * from this exhaustive record (instead of hardcoded prose) keeps the editor's
 * value autocomplete, the system prompt's status hint, and the wire enum in
 * lockstep — adding/renaming a status forces every label to be supplied.
 */
export const DOCUMENT_STATUS_LABELS: Record<GQL.DocumentStatus, string> = {
  [GQL.DocumentStatus.Draft]: 'Draft',
  [GQL.DocumentStatus.Signed]: 'Signed',
  [GQL.DocumentStatus.Template]: 'Template',
  [GQL.DocumentStatus.Expired]: 'Expired',
};

/**
 * Map an extraction question onto the MUI report grid's column type.
 * MultiSelect still uses `singleSelect` in the grid: chips and filters already
 * handle several values on that column type.
 *
 * `DateTime` is not an extraction type, so it never appears here.
 */
export const EXTRACTION_QUESTION_TYPE_TO_REPORT_COLUMN_DATA_TYPE: Record<
  GQL.ExtractionQuestionType,
  GQL.ReportColumnDataType
> = {
  [GQL.ExtractionQuestionType.Text]: GQL.ReportColumnDataType.String,
  [GQL.ExtractionQuestionType.Date]: GQL.ReportColumnDataType.Date,
  [GQL.ExtractionQuestionType.Boolean]: GQL.ReportColumnDataType.Boolean,
  [GQL.ExtractionQuestionType.Number]: GQL.ReportColumnDataType.Number,
  [GQL.ExtractionQuestionType.SingleSelect]: GQL.ReportColumnDataType.SingleSelect,
  [GQL.ExtractionQuestionType.MultiSelect]: GQL.ReportColumnDataType.SingleSelect,
};

export const REPORT_COLUMN_DATA_TYPE_TO_EXTRACTION_QUESTION_TYPE: Record<
  GQL.ReportColumnDataType,
  GQL.ExtractionQuestionType
> = {
  [GQL.ReportColumnDataType.String]: GQL.ExtractionQuestionType.Text,
  [GQL.ReportColumnDataType.Date]: GQL.ExtractionQuestionType.Date,
  [GQL.ReportColumnDataType.DateTime]: GQL.ExtractionQuestionType.Date,
  [GQL.ReportColumnDataType.Boolean]: GQL.ExtractionQuestionType.Boolean,
  [GQL.ReportColumnDataType.Number]: GQL.ExtractionQuestionType.Number,
  [GQL.ReportColumnDataType.SingleSelect]: GQL.ExtractionQuestionType.SingleSelect,
};
